/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        DevOps & Provisioning Zod Schemas
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from 'zod';

/**
 * Cloudflare API account credentials schema.
 */
export const CloudflareCredentialsSchema = z.object({
  accountId: z.string().trim().min(1, 'CLOUDFLARE_ACCOUNT_ID is required'),
  apiToken: z.string().trim().min(1, 'CLOUDFLARE_API_TOKEN is required'),
  zoneId: z.string().trim().optional(),
});

export type CloudflareCredentialsDto = z.infer<typeof CloudflareCredentialsSchema>;

/**
 * Super administrator initial creation schema.
 */
export const SuperAdminSetupSchema = z.object({
  email: z.string().trim().email('Invalid email address format').toLowerCase(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(128, 'Password must not exceed 128 characters'),
  fullName: z.string().trim().min(2, 'Full name must be at least 2 characters long').max(100),
});

export type SuperAdminSetupDto = z.infer<typeof SuperAdminSetupSchema>;

/**
 * Cloudflare provisioned resource IDs manifest schema.
 */
export const ProvisionedResourcesSchema = z.object({
  d1DatabaseId: z.string().uuid(),
  d1DatabaseName: z.string(),
  r2BucketName: z.string(),
  rateLimitKvId: z.string(),
  systemKeysKvId: z.string(),
  vectorizeIndexName: z.string(),
  imageQueueName: z.string(),
  videoQueueName: z.string(),
  provisionedAt: z.string().datetime(),
});

export type ProvisionedResourcesDto = z.infer<typeof ProvisionedResourcesSchema>;
